<template>
  <b-container class="disclaimer">
    <b-row>
      <b-col class="pt-3 pb-2">
        <!-- <b-button variant="outline-primary" class="mr-2" :to="{ name: 'Home' }"
          ><b-icon-arrow-left
        /></b-button> -->
        <strong>Settings</strong>
      </b-col>
    </b-row>
    <b-row v-if="isSmsSettingsReady">
      <b-col>
        <div class="bg-white border rounded px-2">
          <b-row>
            <b-col>
              <div
                class="
                  d-flex
                  justify-content-between
                  align-items-center
                  form-option-container
                "
              >
                <span>I want to receive mobile text message alerts</span>
                <b-form-checkbox
                  v-model="isOptedIn"
                  name="check-button"
                  switch
                  size="lg"
                />
              </div>
            </b-col>
          </b-row>
          <si-phone-number
            v-if="isPhoneNumberVisible"
            :phone-number="phoneNumber"
            :is-verified="isVerified"
            @update="onUpdatePhoneNumber"
          />
          <si-enter-phone
            v-show="isEnterPhoneVisible"
            :show-cancel="phoneNumber.length"
            @continue="onContinueEnterPhone"
            @cancel="onCancelEnterPhone"
          />
          <si-enter-code
            v-show="isEnterCodeVisible"
            :phone-number="newE164PhoneNumber"
            @continue="onContinueEnterCode"
            @go-back="onGoBackEnterCode"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EnterCode from "@/components/settings/sms/EnterCode";
import EnterPhone from "@/components/settings/sms/EnterPhone";
import PhoneNumber from "@/components/settings/sms/PhoneNumber";
import errorHandler from "@/services/errorHandler";
import errorToast from "@/services/errorToast";
import successToast from "@/services/successToast";

/**
 * NOTE:
 * The implementation of the SMS verification process should probably include
 * rate limiting to prevent users from making too many requests to the Twilio
 * API. The mb_members_sms_settings table currently has a verified_count and
 * date_verified columns that may be useful for this functionality; however,
 * the logic will need to be planned out so users are not blocked between the
 * steps of receiving and submitting a code to verify their phone number. If
 * users are limited to, say, 10 API requests per hour and they are on their
 * 9th request and start the verification process, the 11th request would
 * likely occur when they are submitting the verification code, but this
 * request should still go through so as not to unnecessarily preventing them
 * from completing the verification process.
 *
 * NOTE:
 * Any use of a date, such as setting date_verified should use the SERVER time
 * and NOT the user's client time, as users can adjust their computer's date/
 * time.
 */

export default {
  name: "Settings",
  components: {
    "si-enter-code": EnterCode,
    "si-enter-phone": EnterPhone,
    "si-phone-number": PhoneNumber,
  },
  data: () => ({
    isSmsSettingsReady: false,
  }),
  computed: {
    newCode() {
      return this.$store.getters["sms/newCode"];
    },
    newPhoneNumber() {
      return this.$store.getters["sms/newPhoneNumber"];
    },
    newE164PhoneNumber() {
      return this.$store.getters["sms/newE164PhoneNumber"];
    },
    phoneNumber() {
      return this.$store.getters["sms/phoneNumber"];
    },
    isEnterPhoneVisible() {
      return this.$store.getters["sms/isEnterPhoneVisible"];
    },
    isEnterCodeVisible() {
      return this.$store.getters["sms/isEnterCodeVisible"];
    },
    isOptedIn: {
      get: function () {
        return this.$store.getters["sms/isOptedIn"];
      },
      set: function (value) {
        this.$store.dispatch("sms/setIsOptedIn", value).catch((err) => {
          errorHandler(this, err);
        });
      },
    },
    isPhoneNumberVisible() {
      return this.$store.getters["sms/isPhoneNumberVisible"];
    },
    isVerified() {
      return this.$store.getters["sms/isVerified"];
    },
  },
  methods: {
    onCancelEnterPhone() {
      this.$store.dispatch("sms/hideInputs");
    },
    onContinueEnterCode() {
      this.$store.dispatch("showBusy");
      this.$store
        .dispatch("sms/checkCode", {
          phoneNumber: this.newE164PhoneNumber,
          code: this.newCode,
        })
        .then((status) => {
          if (status === "approved") {
            this.$store.dispatch("sms/hideInputs");
            this.$store.dispatch("sms/clearNewPhoneNumber");
            this.$store.dispatch("sms/clearNewE164PhoneNumber");
            this.$store.dispatch("sms/clearNewCode");
            successToast(this, "Verification complete!");
          } else if (status === "pending") {
            errorToast(this, "Invalid code");
          } else if (status === "canceled") {
            errorToast(this, "Expired code");
            this.$store.dispatch("sms/showEnterPhone");
          } else {
            errorToast(this, "An error occurred");
          }
        })
        .catch((err) => {
          errorHandler(this, err);
        })
        .finally(() => {
          this.$store.dispatch("sms/clearNewCode");
          this.$store.dispatch("hideBusy");
        });
    },
    onContinueEnterPhone(phoneNumber) {
      this.$store.dispatch("showBusy");
      this.$store.dispatch("sms/setNewE164PhoneNumber", phoneNumber);
      this.$store
        .dispatch("sms/sendCode", { phoneNumber })
        .then(() => {
          this.$store.dispatch("sms/showEnterCode");
        })
        .catch((err) => {
          errorHandler(this, err);
        })
        .finally(() => {
          this.$store.dispatch("hideBusy");
        });
    },
    onGoBackEnterCode() {
      this.$store.dispatch("sms/showEnterPhone");
    },
    onUpdatePhoneNumber() {
      this.$store.dispatch("sms/showEnterPhone");
    },
  },
  created() {
    this.$store
      .dispatch("sms/fetchSmsSettings")
      .then(() => {
        if (this.isOptedIn && !this.phoneNumber.length) {
          this.$store.dispatch("sms/showEnterPhone");
        }
      })
      .finally(() => {
        this.isSmsSettingsReady = true;
      });
  },
};
</script>
